import { useState } from "react"
import { BackupMethods } from "@portal-hq/web"
import Loader from "@/components/layout/loader"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Progress } from "@/components/ui/progress"
import { Button } from "@/components/ui/button"
import { usePortal } from "@/state/stores/wallet.store"
import { useWalletsActions } from "@/api/wallets"

const PORTAL_BACKUP_STEPS = 6

const methodLabelMap = {
  [BackupMethods.gdrive]: "Google Drive",
  [BackupMethods.passkey]: "Passkey",
}

interface WalletBackupProps {
  onProcessingChange: (processing: boolean) => void;
  method: BackupMethods.gdrive | BackupMethods.passkey;
  onDone?: () => void;
  onSkip?: () => void;
}

export function WalletBackup({
  onProcessingChange,
  method,
  onDone,
  onSkip,
}: WalletBackupProps) {
  const [step, setStep] = useState<"form" | "processing" | "done" | "error">(
    "form",
  )
  const [progress, setProgress] = useState<number>(0)
  const portal = usePortal()
  const { updateBackups } = useWalletsActions()

  if (!portal) {
    return null
  }

  const backup = async () => {
    onProcessingChange(true)
    setStep("processing")
    setProgress(0)

    portal.onReady(async () => {
      try {
        const { cipherText } = await portal.backupWallet(method, () =>
          setProgress((progress) => progress + 1),
        )
        await updateBackups(method, cipherText)
        await portal.storedClientBackupShare(true, method)
        setStep("done")
        onDone?.()
      } catch (err) {
        setStep("error")
      }
      onProcessingChange(false)
    })
  }

  if (step === "form") {
    return (
      <div className="flex flex-col items-start space-y-4">
        <p className="text-sm">
          Using {methodLabelMap[method]} as a backup will allow you to unlock
          your wallet without using your secret.
        </p>
        <Button onClick={() => backup()} className="w-full">
          Backup with {methodLabelMap[method]}
        </Button>
        {onSkip && (
          <Button variant="ghost" className="w-full" onClick={() => onSkip()}>
            Skip for now
          </Button>
        )}
      </div>
    )
  } else if (step === "processing") {
    return (
      <>
        <Loader message="Backing up wallet..." />
        <Progress value={(progress * 100) / PORTAL_BACKUP_STEPS} />
      </>
    )
  } else if (step === "done") {
    return (
      <Alert variant="success">
        <AlertTitle>Done!</AlertTitle>
        <AlertDescription>
          Your wallet has been backed up successfully.
        </AlertDescription>
      </Alert>
    )
  } else if (step === "error") {
    return (
      <Alert variant="destructive">
        <AlertTitle>Backup failed</AlertTitle>
        <AlertDescription>
          Something went wrong while backing up your wallet. Please try again.
        </AlertDescription>
      </Alert>
    )
  }
}
